import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ConnectorLogo from '../components/Connections/ConnectorLogo';
import AuthPanel from '../components/Connections/AuthPanel';
import SetupSteps from '../components/Connections/SetupSteps';
import MappingEditor from '../components/Connections/MappingEditor';
import SpendImportPanel from '../components/Connections/SpendImportPanel';
import { Button } from '../components/ui/button';
import { Actions as CONN_ACTIONS } from '../store/actions/connectionActions';

const LIVE_STATUSES = new Set(['connected', 'needs_organisation']);

const ConnectionDetail = ({
  descriptor,
  catalog,
  instances,
  externalAccounts,
  loading,
  error,
  canManage,
  currentOrganisation,
  getConnectionDescriptor,
  getConnectionCatalog,
  listConnections,
  getExternalAccounts,
}) => {
  const { connectorId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const oauthReturn = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      connectionInstanceId: params.get('connectionInstanceId'),
      setupStepsPending: params.get('setupStepsPending'),
      statusHint: params.get('status'),
    };
  }, [location.search]);

  const justReturnedFromOAuth = Boolean(oauthReturn.connectionInstanceId);

  useEffect(() => {
    if (!canManage || !connectorId) return undefined;

    let cancelled = false;
    const refresh = async () => {
      const desc = await getConnectionDescriptor(connectorId);
      if (cancelled) return;
      await listConnections();
      if (cancelled) return;
      if (!catalog?.length) await getConnectionCatalog();
      if (cancelled) return;
      if (justReturnedFromOAuth && desc && LIVE_STATUSES.has(desc.status)) {
        history.replace(`/connections/${connectorId}`);
      }
    };
    refresh();

    return () => {
      cancelled = true;
    };
  }, [connectorId, canManage]);

  const catalogEntry = useMemo(
    () => (catalog || []).find((c) => c.connectorId === connectorId || c.id === connectorId),
    [catalog, connectorId],
  );

  const instance = useMemo(
    () => (instances || []).find((c) => c.connectorId === connectorId),
    [instances, connectorId],
  );

  /**
   * Prefer descriptor; if missing, merge catalog + instance list.
   * Never invent "disconnected" when the instances API already says otherwise.
   * Always prefer instance tenant fields when descriptor omits them.
   */
  const view = useMemo(() => {
    if (descriptor) {
      return {
        ...descriptor,
        externalTenantName:
          descriptor.externalTenantName ||
          descriptor.externalOrganisationName ||
          instance?.externalTenantName ||
          null,
        externalTenantId:
          descriptor.externalTenantId ||
          descriptor.externalOrganisationId ||
          instance?.externalTenantId ||
          null,
      };
    }
    if (!catalogEntry) return null;
    return {
      ...catalogEntry,
      connectorId: catalogEntry.connectorId || catalogEntry.id || connectorId,
      status: instance?.status || 'disconnected',
      externalTenantName: instance?.externalTenantName || null,
      externalTenantId: instance?.externalTenantId || null,
      needsExternalOrganisationSelection: instance?.needsExternalOrganisationSelection,
      hasMappingConfig: instance?.hasMappingConfig,
      lastError: instance?.lastError || null,
      connectionInstanceId: instance?.id || oauthReturn.connectionInstanceId,
      setupSteps: [],
      auth: catalogEntry.auth || { strategy: 'oauth' },
      ingest: null,
      capabilities: [],
    };
  }, [descriptor, catalogEntry, instance, connectorId, oauthReturn.connectionInstanceId]);

  // When live but tenant name missing, load external-accounts so we can still show the linked org.
  useEffect(() => {
    if (!connectorId || !view) return;
    if (!LIVE_STATUSES.has(view.status)) return;
    if (view.externalTenantName) return;
    if (externalAccounts?.selectedId || externalAccounts?.accounts?.length) return;
    getExternalAccounts(connectorId, `/my/connections/${connectorId}/external-accounts`);
  }, [
    connectorId,
    view?.status,
    view?.externalTenantName,
    externalAccounts?.selectedId,
    externalAccounts?.accounts?.length,
    getExternalAccounts,
  ]);

  const linkedOrgLabel = useMemo(() => {
    if (view?.externalTenantName) return view.externalTenantName;
    const selected = (externalAccounts?.accounts || []).find((a) => a.id === externalAccounts?.selectedId);
    return selected?.label || null;
  }, [view, externalAccounts]);

  const viewWithOrg = useMemo(() => {
    if (!view) return null;
    if (view.externalTenantName) return view;
    if (!linkedOrgLabel) return view;
    return {
      ...view,
      externalTenantName: linkedOrgLabel,
    };
  }, [view, linkedOrgLabel]);

  if (!canManage) {
    return (
      <div className='p-6'>
        <p className='text-muted-foreground'>You need organisation manage permission to manage connections.</p>
      </div>
    );
  }

  if (!connectorId) {
    return (
      <div className='space-y-4 p-6'>
        <Link to='/connections' className='inline-flex items-center gap-1 text-sm text-primary'>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Connections
        </Link>
        <p className='text-muted-foreground'>Missing connector id in the URL.</p>
      </div>
    );
  }

  if ((loading?.descriptor || loading?.list) && !viewWithOrg) {
    return <div className='p-6 text-muted-foreground'>Loading connection…</div>;
  }

  if (!viewWithOrg) {
    return (
      <div className='space-y-4 p-6'>
        <Link to='/connections' className='inline-flex items-center gap-1 text-sm text-primary'>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Connections
        </Link>
        <p className='font-medium text-foreground'>Could not load this connector</p>
        <p className='text-sm text-muted-foreground'>
          {error?.message ||
            `No descriptor for “${connectorId}”. Check the network tab for GET /my/connections/${connectorId}.`}
        </p>
        <Button variant='outline' loading={loading?.descriptor} onClick={() => getConnectionDescriptor(connectorId)}>
          Retry
        </Button>
      </div>
    );
  }

  const setupSteps =
    viewWithOrg.setupSteps?.length > 0
      ? viewWithOrg.setupSteps
      : viewWithOrg.status === 'needs_organisation' || viewWithOrg.needsExternalOrganisationSelection
        ? [
            {
              id: 'select-external-account',
              type: 'selectExternalAccount',
              required: true,
              uiLabel: 'Organisation',
              optionsUrl: `/my/connections/${connectorId}/external-accounts`,
              submitUrl: `/my/connections/${connectorId}/external-accounts`,
            },
          ]
        : [];
  const showSetup = setupSteps.length > 0;
  const isLive = LIVE_STATUSES.has(viewWithOrg.status);
  const showMapping = Boolean(descriptor) && isLive && viewWithOrg.connectable !== false;
  const showImport = Boolean(descriptor) && isLive;

  const oauthStatusMismatch =
    justReturnedFromOAuth &&
    !loading?.descriptor &&
    descriptor &&
    !LIVE_STATUSES.has(descriptor.status);

  return (
    <div className='space-y-6 p-6'>
      <div className='space-y-3'>
        <Link to='/connections' className='inline-flex items-center gap-1 text-sm text-primary'>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Connections
        </Link>
        <div className='flex items-center gap-3'>
          <ConnectorLogo
            logoUrl={viewWithOrg.logoUrl}
            logoAlt={viewWithOrg.logoAlt}
            logoDarkUrl={viewWithOrg.logoDarkUrl}
            className='h-12 w-12'
          />
          <div>
            <h1 className='text-2xl font-semibold text-foreground'>{viewWithOrg.label || connectorId}</h1>
            <p className='text-sm text-muted-foreground'>{viewWithOrg.vertical}</p>
          </div>
        </div>
      </div>

      {oauthStatusMismatch && (
        <div className='rounded-md border border-warning bg-warning-bg p-3 text-sm text-warning space-y-2'>
          <p className='font-medium'>OAuth returned successfully, but this org still looks disconnected.</p>
          <p>
            Return params included <code className='text-xs'>connectionInstanceId={oauthReturn.connectionInstanceId}</code>
            {oauthReturn.setupStepsPending != null ? (
              <>
                {' '}
                and <code className='text-xs'>setupStepsPending={oauthReturn.setupStepsPending}</code>
              </>
            ) : null}
            , yet <code className='text-xs'>GET /my/connections/{connectorId}</code> reports{' '}
            <code className='text-xs'>status={descriptor.status || 'unknown'}</code>
            {currentOrganisation ? (
              <>
                {' '}
                for organisation header <code className='text-xs'>{String(currentOrganisation)}</code>
              </>
            ) : (
              ' (no current organisation in FE state)'
            )}
            .
          </p>
          <p>
            Ask backend: was the dual-write for this instance bound to the same org id the FE sends on descriptor/list?
            Check response of GET descriptor and GET /my/connections in the network tab.
          </p>
          <Button
            variant='outline'
            size='sm'
            loading={loading?.descriptor}
            onClick={() => {
              getConnectionDescriptor(connectorId);
              listConnections();
            }}>
            Refresh status
          </Button>
        </div>
      )}

      {error && !descriptor && (
        <div className='rounded-md border border-warning bg-warning-bg p-3 text-sm text-warning'>
          Could not load full connection details ({error.message || error.code || 'request failed'}). You can still try
          Connect; mapping and import need a successful descriptor response.
          <Button variant='ghost' size='sm' className='ml-2' onClick={() => getConnectionDescriptor(connectorId)}>
            Retry
          </Button>
        </div>
      )}

      <section className='rounded-xl border border-border bg-card p-4'>
        <AuthPanel connectorId={connectorId} descriptor={viewWithOrg} />
      </section>

      {showSetup && <SetupSteps connectorId={connectorId} setupSteps={setupSteps} />}

      {!isLive && descriptor && viewWithOrg.connectable !== false && !oauthStatusMismatch && (
        <p className='text-sm text-muted-foreground rounded-md border border-border bg-muted/40 px-4 py-3'>
          Connect this account to configure dimension mapping and import spend. Mapping is saved on the connection and
          kept if you disconnect later.
        </p>
      )}

      {showMapping && <MappingEditor connectorId={connectorId} />}

      {showImport && (
        <SpendImportPanel connectorId={connectorId} descriptor={viewWithOrg} />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  descriptor: state.connections?.descriptor,
  catalog: state.connections?.catalog,
  instances: state.connections?.list,
  externalAccounts: state.connections?.externalAccounts,
  loading: state.connections?.loading,
  error: state.connections?.error,
  currentOrganisation: state.currentOrganisation,
  canManage: !!state?.organisation?.permissions?.organisation?.manage || !!state.profile?.details?.cyfAdmin,
});

const mapDispatchToProps = (dispatch) => ({
  getConnectionDescriptor: (id) => dispatch(CONN_ACTIONS.getConnectionDescriptor(id)),
  getConnectionCatalog: () => dispatch(CONN_ACTIONS.getConnectionCatalog()),
  listConnections: () => dispatch(CONN_ACTIONS.listConnections()),
  getExternalAccounts: (id, url) => dispatch(CONN_ACTIONS.getExternalAccounts(id, url)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConnectionDetail);
